/**
 * Chat calls: ring, answer, talk — and, once someone is pulled in, keep talking
 * as a group.
 *
 * Two transports, on purpose:
 *  - the RING (incoming / cancelled / declined / invited) rides the ERP socket,
 *    because it must reach a colleague who merely has the ERP open in some tab;
 *  - the call itself rides a short-lived socket to the 676767 calls service,
 *    which also mints the TURN credentials. Media is peer-to-peer for two
 *    people and goes through the Cloudflare SFU once there are more; neither
 *    passes through our backend.
 *
 * The engine is not ours and is not copied here: it is loaded from the calls
 * service (`features/calls/sdk.ts`), which is what keeps one implementation of
 * calls in existence rather than a copy per app. Everything below is the ERP's
 * own part — who to ring, and what it looks like.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Link2,
  Monitor,
  MonitorOff,
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  UserPlus,
  Video,
  VideoOff,
  X,
} from "lucide-react";
import type {
  CallsRoom,
  CallState,
  PeerMediaStatus,
  RoomParticipant,
} from "calls-sdk";
import { useAppSelector } from "hooks";
import { socket } from "services/socket-client";
import {
  createGuestLink,
  fetchCallToken,
  fetchCallsConfig,
  inviteToCall,
  sendCallSignal,
  sendCallSignalBeacon,
} from "services/calls.service";
import { showBrowserNotification } from "features/chat/browser-notifications";
import { loadCallsSdk, type CallsSdkModule } from "./sdk";
import { AudioSink, CallStage, PeerTile, canShareScreen } from "./call-tiles";
import {
  CallFailurePanel,
  MicHelpSteps,
  type CallFailure,
} from "./call-failure";

export interface CallPeer {
  userId: string;
  name: string;
}

interface ActiveCall {
  callId: string;
  companyId: string;
  conversationId: string;
  /** the other side of a 1:1 call; null when we were invited into a group */
  peer: CallPeer | null;
  outgoing: boolean;
  /** rings as "join us", not "someone is calling you" */
  invited: boolean;
  /** the other side's page is reloading — hold the line, they are coming back */
  reconnecting: boolean;
  group: boolean;
  /** a roster has actually arrived — until it does, the person we called is
   *  still the person on the call */
  rosterSeen: boolean;
  state: CallState;
  /** reached 'connected' at least once — a connection lost after that is a
   *  dropped call, not a network that never let the call through */
  everConnected: boolean;
  /** how far the other side of a 1:1 has got with its own microphone prompt;
   *  null until it says (and always in a group call) */
  peerMedia: PeerMediaStatus | null;
  muted: boolean;
  videoOn: boolean;
  sharing: boolean;
  participants: RoomParticipant[];
}

interface CallsContextValue {
  /** false when the deployment has no calls service configured */
  enabled: boolean;
  /** conversation currently on a call, so a chat window can show it */
  busyConversationId: string | null;
  startCall: (
    companyId: string,
    conversationId: string,
    peer: CallPeer
  ) => void;
}

const CallsContext = createContext<CallsContextValue>({
  enabled: false,
  busyConversationId: null,
  startCall: () => {},
});

export const useCalls = (): CallsContextValue => useContext(CallsContext);

interface IncomingPayload {
  conversationId: string;
  companyId: string;
  callId: string;
  kind?: string;
  reason?: string;
  group?: boolean;
  from: { userId: string; name: string };
}

// An unanswered call gives up on its own — the same 45s the ringtone is capped
// at, so a lost hangup can never leave a phone ringing forever.
const RING_TIMEOUT_MS = 45000;
// Backstop for every other way a call can fail to become a call: the peer's tab
// dies without sending anything, the accept never reaches the caller, our own
// microphone prompt is never answered. Without it the UI sits on "Connecting…"
// until the page reloads. The engine has its own, better-informed connect
// deadline (at most 60 s, and it can say WHY it gave up), so this one is set
// well past it and only catches what the engine cannot see.
const STUCK_TIMEOUT_MS = 90000;
// An engine older than that deadline (no CONNECT_TIMEOUT_MS) gives up on
// nothing by itself, so this backstop is the only thing that ends the wait —
// no reason to make it any longer than it always was.
const STUCK_TIMEOUT_LEGACY_MS = 60000;
// How long past the caller's own no-answer timeout an incoming ring screen may
// outlive it: room for the caller's cancel to arrive by itself first.
const INCOMING_GRACE_MS = 15000;
// Chrome answers a remembered "allow" in a few milliseconds; a prompt still open
// after this long is one the person is either reading or cannot see.
const PERMISSION_HINT_DELAY_MS = 2000;
// A remembered "allow" still passes through the permission state for 100–300
// ms; naming the prompt only after this long keeps that from flashing a
// "waiting for permission" label on every single call.
const PERMISSION_LABEL_DELAY_MS = 500;
// Every tab of the same person receives the ring (it is broadcast to their
// user room), so the tabs have to agree on who took it. Whoever answers,
// declines or lets it lapse says so here and the others drop the call screen
// without touching the caller — otherwise a forgotten background tab would
// later "decline" a conversation its sibling is in the middle of.
const TAB_CHANNEL = "erp-calls";
// A group call whose roster has gone empty is over — but not the instant it
// reads empty: one person's second tab handing the session over re-broadcasts
// the roster, and a reconnect can land between two of them. Long enough to tell
// those apart, short enough that nobody is left sitting in a call alone (and
// therefore turning away everyone who tries to ring them back).
const EMPTY_ROSTER_GRACE_MS = 4000;
// A reload takes a few seconds; the other side holds the line that long before
// giving up on someone coming back.
const RESUME_WINDOW_MS = 30000;
// What a reloading tab leaves behind for its own next life. sessionStorage, not
// localStorage: a call belongs to the tab that was in it, and a NEW tab opening
// the ERP must never walk into a conversation it was not part of.
const RESUME_KEY = "erp-call-resume";
// A `call:ended` may be the peer hanging up — or their page reloading, whose
// "hold the line" word travels by our own backend and can land a moment later.
// Long enough for the two to be told apart, short enough to not look stuck.
const ENDED_GRACE_MS = 1200;

interface ResumeRecord {
  callId: string;
  companyId: string;
  conversationId: string;
  peer: CallPeer | null;
  group: boolean;
  at: number;
}

function rememberCall(call: ActiveCall): void {
  try {
    const record: ResumeRecord = {
      callId: call.callId,
      companyId: call.companyId,
      conversationId: call.conversationId,
      peer: call.peer,
      group: call.group,
      at: Date.now(),
    };
    window.sessionStorage.setItem(RESUME_KEY, JSON.stringify(record));
  } catch {
    /* storage disabled — the call simply will not come back by itself */
  }
}

function takeRememberedCall(): ResumeRecord | null {
  try {
    const raw = window.sessionStorage.getItem(RESUME_KEY);
    window.sessionStorage.removeItem(RESUME_KEY); // one attempt, never a loop
    if (!raw) return null;
    const record = JSON.parse(raw) as ResumeRecord;
    if (!record?.callId || Date.now() - record.at > RESUME_WINDOW_MS)
      return null;
    return record;
  } catch {
    return null;
  }
}

function forgetCall(): void {
  try {
    window.sessionStorage.removeItem(RESUME_KEY);
  } catch {
    /* nothing to forget */
  }
}

export function CallsProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const session = useAppSelector((state) => state.authReducer.session);
  const myUserId = session?.id ? String(session.id) : "";
  const onlineUsers = useAppSelector((state) => state.presenceReducer.online);

  const [enabled, setEnabled] = useState(false);
  const [call, setCall] = useState<ActiveCall | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [streams, setStreams] = useState<Record<string, MediaStream>>({});
  const [peerVideo, setPeerVideo] = useState<Record<string, boolean>>({});
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [failure, setFailure] = useState<CallFailure | null>(null);

  const sdkRef = useRef<CallsSdkModule | null>(null);
  const sdkUrlRef = useRef<string | null>(null);
  const roomRef = useRef<CallsRoom | null>(null);
  // The connection a resumed call is replacing. It is kept open until the new
  // one is up: the calls service takes a person's LAST socket leaving the room
  // as them hanging up and tells everyone so — and "everyone" includes the
  // very page that just came back to ring us. Closing first, then opening,
  // ended every resumed call within a second of its ring.
  const staleRoomRef = useRef<CallsRoom | null>(null);
  // Bumped by every connect: a room that was replaced keeps firing its
  // handlers (its own hang-up, for one), and they carry the same call id as
  // the room that replaced it, so the id alone cannot tell them apart.
  const connectGenRef = useRef(0);
  const callRef = useRef<ActiveCall | null>(null);
  const ringTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emptyRosterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const endedGraceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    callRef.current = call;
  }, [call]);

  // Cross-tab coordination. `postMessage` never reaches the sender, so a tab
  // only ever hears about its siblings.
  const tabChannelRef = useRef<BroadcastChannel | null>(null);
  const announceToTabs = useCallback((callId: string) => {
    try {
      tabChannelRef.current?.postMessage({ handledCallId: callId });
    } catch {
      /* no BroadcastChannel (older Safari) — the sibling tabs just keep their
         own screen until it lapses, which is the pre-BroadcastChannel behaviour */
    }
  }, []);

  // The engine is fetched as soon as we know calls exist here, not when one
  // starts: an incoming ring has to make a sound immediately, and the ringtone
  // lives in it.
  useEffect(() => {
    let alive = true;
    void fetchCallsConfig().then(async (cfg) => {
      if (!alive || !cfg.enabled || !cfg.sdkUrl) return;
      sdkUrlRef.current = cfg.sdkUrl;
      try {
        sdkRef.current = await loadCallsSdk(cfg.sdkUrl);
        if (alive) setEnabled(true);
      } catch {
        // the calls service is unreachable — better no call button than one
        // that can only fail
        if (alive) setEnabled(false);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  /** Tear everything down. `notifyPeer` sends the media-level hangup; the
   *  ERP-level cancel/decline is sent by the callers of this. */
  const teardown = useCallback((notifyPeer: boolean) => {
    sdkRef.current?.stopRingtone();
    if (ringTimerRef.current) {
      clearTimeout(ringTimerRef.current);
      ringTimerRef.current = null;
    }
    if (emptyRosterTimerRef.current) {
      clearTimeout(emptyRosterTimerRef.current);
      emptyRosterTimerRef.current = null;
    }
    if (endedGraceRef.current) {
      clearTimeout(endedGraceRef.current);
      endedGraceRef.current = null;
    }
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    // a call that really ended must not come back on the next reload
    forgetCall();
    const current = callRef.current;
    const room = roomRef.current;
    roomRef.current = null;
    void room?.hangUp(notifyPeer);
    const stale = staleRoomRef.current;
    staleRoomRef.current = null;
    void stale?.hangUp(false);
    // Let the backend forget we were in this call, or it would keep admitting
    // us (and anyone we invited) to the room. ONLY if this tab was actually in
    // it: the ring reaches every tab of ours, and a sibling that merely showed
    // the screen — or let it lapse — must not check the person out of a call
    // another tab is in the middle of.
    if (current && room) {
      void sendCallSignal(current.companyId, current.conversationId, {
        type: "end",
        callId: current.callId,
      }).catch(() => {});
    }
    setSeconds(0);
    setStreams({});
    setPeerVideo({});
    setLocalStream(null);
    setMinimized(false);
    setCall(null);
    callRef.current = null;
  }, []);

  /** Leave the failure panel behind for the call that is about to be torn
   *  down. Must run BEFORE the teardown: the relay servers the call was given
   *  are what the network check tests, and they go with the room. */
  const recordFailure = useCallback((kind: CallFailure["kind"]) => {
    const current = callRef.current;
    if (!current) return;
    let iceServers: RTCIceServer[] | null = null;
    try {
      iceServers = roomRef.current?.iceServers ?? null;
    } catch {
      /* an older engine without the getter — the check will say it can't run */
    }
    setFailure({
      kind,
      // a group has no one person to ring back; the "peer" of an invite is
      // only whoever sent it
      peer: current.group || current.invited ? null : current.peer,
      companyId: current.companyId,
      conversationId: current.conversationId,
      iceServers,
      at: Date.now(),
    });
  }, []);

  /** Open the signaling connection for this call and build the engine. Returns
   *  null when the call was hung up while this was in flight — whatever was
   *  opened is closed here rather than left for the next call to inherit. */
  const connect = useCallback(
    async (
      companyId: string,
      conversationId: string,
      callId: string
    ): Promise<CallsRoom | null> => {
      // normally loaded at startup; a ring that arrives while that is still in
      // flight (or after it failed once) waits for it here rather than leaving
      // the call screen stuck on "Connecting…"
      const sdk =
        sdkRef.current ??
        (sdkUrlRef.current ? await loadCallsSdk(sdkUrlRef.current) : null);
      if (!sdk) throw new Error("calls engine unavailable");
      sdkRef.current = sdk;
      const gen = ++connectGenRef.current;
      // this call, on THIS connection: a room that has since been replaced
      // (a resume opens a new one) must not speak for the call any more
      const alive = () =>
        callRef.current?.callId === callId && connectGenRef.current === gen;
      const minted = await fetchCallToken(companyId, conversationId, callId);
      if (!alive()) return null;

      const room = await sdk.CallsRoom.connect({
        url: minted.url,
        token: minted.token,
        handlers: {
          onState: (s) => {
            if (!alive()) return;
            setCall((c) =>
              c
                ? {
                    ...c,
                    state: s,
                    everConnected: c.everConnected || s === "connected",
                  }
                : c
            );
          },
          onParticipants: (list) => {
            if (!alive()) return;
            setCall((c) =>
              c
                ? {
                    ...c,
                    participants: list,
                    rosterSeen: c.rosterSeen || list.length > 0,
                    // Somebody is in the room again — whoever we were holding
                    // the line for has finished reloading and walked back in.
                    // A group call says so this way; it never rings.
                    reconnecting: list.length > 0 ? false : c.reconnecting,
                  }
                : c
            );
            if (emptyRosterTimerRef.current) {
              clearTimeout(emptyRosterTimerRef.current);
              emptyRosterTimerRef.current = null;
            }
            if (list.length > 0) {
              if (resumeTimerRef.current) {
                clearTimeout(resumeTimerRef.current);
                resumeTimerRef.current = null;
              }
              return;
            }
            // Everyone else has left. Hanging on would leave this person alone
            // in a call that no longer exists — and, because a busy line turns
            // away every new ring, unreachable until they reload.
            const giveUp = (wait: number) => {
              emptyRosterTimerRef.current = setTimeout(() => {
                emptyRosterTimerRef.current = null;
                const cur = callRef.current;
                if (!cur || cur.callId !== callId) return;
                if (!cur.group || !cur.rosterSeen || cur.participants.length > 0) {
                  return;
                }
                // A reload empties the roster before its "hold the line" can
                // reach us, so that flag is read HERE rather than when the
                // timer was set — and once it is on, we wait the whole window.
                if (cur.reconnecting) {
                  giveUp(RESUME_WINDOW_MS);
                  return;
                }
                toast.info(t("call_everyone_left"));
                teardown(false);
              }, wait);
            };
            giveUp(EMPTY_ROSTER_GRACE_MS);
          },
          onMode: (mode) => {
            if (alive())
              setCall((c) => (c ? { ...c, group: mode === "group" } : c));
          },
          onStream: (peerId, stream) => {
            if (alive()) setStreams((prev) => ({ ...prev, [peerId]: stream }));
          },
          onStreamGone: (peerId) => {
            if (!alive()) return;
            setStreams((prev) => {
              const next = { ...prev };
              delete next[peerId];
              return next;
            });
          },
          onLocalVideo: (track) => {
            if (!alive()) return;
            setLocalStream(track ? new MediaStream([track]) : null);
            // Stopping a share from the browser's own "Stop sharing" bar never
            // goes through our buttons, so read the state back off the engine
            // rather than trusting what the last click set.
            const room = roomRef.current;
            setCall((c) =>
              c
                ? { ...c, videoOn: !!room?.videoOn, sharing: !!room?.sharing }
                : c
            );
          },
          onPeerVideo: (peerId, on) => {
            if (alive()) setPeerVideo((prev) => ({ ...prev, [peerId]: on }));
          },
          onPeerMedia: (peerId, status) => {
            if (!alive()) return;
            // only the person this 1:1 is with; a stray id is not our business
            setCall((c) =>
              c && (!c.peer || c.peer.userId === peerId)
                ? { ...c, peerMedia: status }
                : c
            );
          },
          onEnded: (reason) => {
            if (!alive()) return;
            if (reason === "timeout") {
              // The other side gave up waiting for us. If our own microphone
              // prompt is still open, that prompt is why — say so, not
              // "network".
              recordFailure(
                callRef.current?.state === "permission" ? "mic-pending" : "network"
              );
              teardown(false);
              return;
            }
            const current = callRef.current;
            if (
              reason === "mic" ||
              (reason === "peer" &&
                current &&
                current.state !== "connected" &&
                !current.everConnected &&
                current.peerMedia === "permission")
            ) {
              // Their microphone was refused — said outright by the engine
              // ('mic'), or, from an engine that does not say, what a hang-up
              // during their open prompt looks like from here. Explain it
              // rather than let the call screen just vanish.
              recordFailure("peer-permission");
              teardown(false);
              return;
            }
            if (reason === "busy") toast.info(t("call_busy"));
            else if (reason === "declined") toast.info(t("call_declined"));
            else if (reason === "unavailable") toast.info(t("call_offline"));
            // "was talking" rather than "is connected": an engine that ends
            // its own leg on the peer's `ended` reports that state a moment
            // before this, and a reload must still be told apart from a hang-up
            const wasTalking =
              current?.state === "connected" || !!current?.everConnected;
            if (reason !== "peer" || !wasTalking) {
              teardown(false);
              return;
            }
            // Their socket is gone — which is what a hang-up AND a reload both
            // look like from here. Wait out the difference: a reloading tab's
            // word travels by our own backend and may land a moment after this.
            if (endedGraceRef.current) clearTimeout(endedGraceRef.current);
            endedGraceRef.current = setTimeout(() => {
              endedGraceRef.current = null;
              // the room that asked may have been replaced since (their page
              // came back and a resume opened a new one): its question is moot
              if (!alive()) return;
              const cur = callRef.current;
              if (!cur || cur.reconnecting) return;
              teardown(false);
            }, ENDED_GRACE_MS);
          },
          onError: (kind, detail) => {
            if (!alive()) return;
            if (kind === "camera") {
              // the camera is an extra, not the call: a denied prompt or a
              // missing device leaves the voices talking
              toast.error(t("call_camera_denied"));
              setCall((c) =>
                c ? { ...c, videoOn: false, sharing: false } : c
              );
              return;
            }
            // 'signaling' means their SDP never arrived: their media never
            // came up, which in practice is their microphone prompt — unless
            // they had already told us their microphone was ready. 'ice'
            // means both sides had media and no path was found between them.
            // A 'connection' error on a call that WAS talking is the line
            // dropping mid-call, not a firewall that never let it through.
            const current = callRef.current;
            recordFailure(
              kind === "mic"
                ? "mic"
                : kind === "timeout" &&
                  detail?.stage === "signaling" &&
                  (detail.peerPermission || current?.peerMedia !== "ready")
                ? "peer-permission"
                : kind === "connection" && current?.everConnected
                ? "dropped"
                : "network"
            );
            teardown(false);
          },
        },
      });
      if (!alive()) {
        void room.hangUp(false);
        return null;
      }
      roomRef.current = room;
      // only now is the connection we were holding for a resume let go: we are
      // in the room on the new socket, so its leaving is not our leaving
      const stale = staleRoomRef.current;
      staleRoomRef.current = null;
      void stale?.hangUp(false);
      return room;
    },
    [recordFailure, t, teardown]
  );

  const startCall = useCallback(
    (
      companyId: string,
      conversationId: string,
      peer: CallPeer,
      /** Re-placing a call a reload interrupted: the SAME id, which is how the
       *  other side knows to pick it up in silence instead of ringing. */
      resumeCallId?: string
    ) => {
      if (callRef.current) {
        toast.info(t("call_already_in_call"));
        return;
      }
      if (!enabled || !peer.userId) return;
      // a new attempt is the answer to whatever the last one left on screen
      setFailure(null);
      // the AudioContext and the notification permission both want a gesture,
      // and this click is the only one we are guaranteed to get
      sdkRef.current?.unlockSound();
      const callId =
        resumeCallId ??
        (typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : String(Date.now()));
      const next: ActiveCall = {
        callId,
        companyId,
        conversationId,
        peer,
        outgoing: true,
        invited: false,
        reconnecting: false,
        group: false,
        rosterSeen: false,
        state: "ringing",
        everConnected: false,
        peerMedia: null,
        muted: false,
        videoOn: false,
        sharing: false,
        participants: [],
      };
      setCall(next);
      callRef.current = next;

      void (async () => {
        try {
          const room = await connect(companyId, conversationId, callId);
          if (!room || callRef.current?.callId !== callId) return; // cancelled meanwhile
          room.startOutgoing(peer.userId);
          const { delivered } = await sendCallSignal(
            companyId,
            conversationId,
            {
              type: "ring",
              callId,
              kind: "audio",
            }
          );
          if (callRef.current?.callId !== callId) return;
          if (!delivered) {
            toast.info(t("call_offline"));
            teardown(false);
            return;
          }
          ringTimerRef.current = setTimeout(() => {
            const cur = callRef.current;
            // only an UNANSWERED call gives up here: by now the peer may have
            // accepted, and a timer that fires regardless would hang up every
            // call at 45 seconds
            if (cur?.callId !== callId || cur.state !== "ringing") return;
            void sendCallSignal(companyId, conversationId, {
              type: "cancel",
              callId,
            }).catch(() => {});
            toast.info(t("call_no_answer"));
            teardown(false);
          }, RING_TIMEOUT_MS);
        } catch {
          if (callRef.current?.callId !== callId) return;
          toast.error(t("call_failed"));
          teardown(false);
        }
      })();
    },
    [connect, enabled, t, teardown]
  );

  const accept = useCallback(() => {
    const current = callRef.current;
    if (!current || current.outgoing) return;
    sdkRef.current?.stopRingtone();
    sdkRef.current?.unlockSound();
    announceToTabs(current.callId);
    setCall({ ...current, state: "connecting" });
    void (async () => {
      try {
        const room = await connect(
          current.companyId,
          current.conversationId,
          current.callId
        );
        if (!room || callRef.current?.callId !== current.callId) return;
        if (current.invited) {
          // joining something already happening: there is no one peer to answer
          const ok = await room.joinGroup();
          if (!ok) throw new Error("join failed");
        } else if (current.peer) {
          await room.answer(current.peer.userId);
        }
      } catch {
        if (callRef.current?.callId !== current.callId) return;
        toast.error(t("call_failed"));
        void sendCallSignal(current.companyId, current.conversationId, {
          type: "decline",
          callId: current.callId,
          to: current.peer?.userId,
        }).catch(() => {});
        teardown(false);
      }
    })();
  }, [announceToTabs, connect, t, teardown]);

  /** Their page came back and is ringing the same call again. Pick it up
   *  without a ring: as far as either person is concerned, the conversation
   *  never stopped — only the tab did. */
  const resumeAsCallee = useCallback(
    (data: IncomingPayload) => {
      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = null;
      }
      // "was that a hang-up or a reload?" is answered: they are back
      if (endedGraceRef.current) {
        clearTimeout(endedGraceRef.current);
        endedGraceRef.current = null;
      }
      // The session on the other end died with their page; this one is new.
      // Ours is set aside rather than closed: it is closed once the new one is
      // connected (see `connect`), so that our socket count in the room never
      // touches zero — the moment it does, the calls service tells the room we
      // hung up, and the page that just came back believes it. A resume that
      // lands while the previous one is still connecting has no live room to
      // set aside and leaves the held one where it is: closing it now would be
      // that very early close.
      const live = roomRef.current;
      if (live) {
        const prev = staleRoomRef.current;
        staleRoomRef.current = live;
        roomRef.current = null;
        void prev?.hangUp(false);
      }
      setStreams({});
      setPeerVideo({});
      setLocalStream(null);
      const next: ActiveCall = {
        callId: String(data.callId),
        companyId: String(data.companyId),
        conversationId: String(data.conversationId),
        peer: { userId: String(data.from.userId), name: data.from.name || "" },
        outgoing: false,
        invited: false,
        reconnecting: false,
        group: !!data.group,
        rosterSeen: false,
        state: "connecting",
        everConnected: false,
        peerMedia: null,
        muted: false,
        videoOn: false,
        sharing: false,
        participants: [],
      };
      setCall(next);
      callRef.current = next;
      accept();
    },
    [accept]
  );

  /** Our own way back in after a reload, when the call was a group: nobody
   *  needs to be rung, the room is still there and still ours. */
  const resumeGroup = useCallback(
    (record: ResumeRecord) => {
      const next: ActiveCall = {
        callId: record.callId,
        companyId: record.companyId,
        conversationId: record.conversationId,
        peer: record.peer,
        outgoing: true,
        invited: false,
        reconnecting: false,
        group: true,
        rosterSeen: false,
        state: "connecting",
        everConnected: false,
        peerMedia: null,
        muted: false,
        videoOn: false,
        sharing: false,
        participants: [],
      };
      setCall(next);
      callRef.current = next;
      void (async () => {
        try {
          const room = await connect(
            record.companyId,
            record.conversationId,
            record.callId
          );
          if (!room || callRef.current?.callId !== record.callId) return;
          const ok = await room.joinGroup();
          if (!ok) throw new Error("join failed");
        } catch {
          if (callRef.current?.callId !== record.callId) return;
          teardown(false);
        }
      })();
    },
    [connect, teardown]
  );

  /** Hang up / decline / cancel — the same button at every stage.
   *  The ERP-level signal is only for a call that has not connected yet: once
   *  media is flowing, the hangup reaches the peer over the call socket, and
   *  sending "cancelled" on top of it would show them a missed call they just
   *  had. */
  const decline = useCallback(() => {
    const current = callRef.current;
    if (!current) return;
    announceToTabs(current.callId);
    // Anything short of a connected call still needs the app-level word: the
    // peer may not even be on the signaling socket yet (a callee joins it only
    // on accept), so the call-socket hangup would reach nobody.
    if (current.state !== "connected") {
      void sendCallSignal(current.companyId, current.conversationId, {
        type: current.outgoing ? "cancel" : "decline",
        callId: current.callId,
        to: current.peer?.userId,
      }).catch(() => {});
    }
    teardown(true);
  }, [announceToTabs, teardown]);

  const toggleMute = useCallback(() => {
    const muted = roomRef.current?.toggleMute() ?? false;
    setCall((c) => (c ? { ...c, muted } : c));
  }, []);

  const toggleCamera = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    void (async () => {
      if (room.videoOn && !room.sharing) await room.stopVideo();
      else await room.enableCamera();
      setCall((c) =>
        c ? { ...c, videoOn: room.videoOn, sharing: room.sharing } : c
      );
    })();
  }, []);

  const toggleScreen = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    void (async () => {
      try {
        if (room.sharing) await room.stopVideo();
        else await room.shareScreen();
      } catch {
        // the picker was dismissed, or the browser has no getDisplayMedia
        toast.info(t("call_screen_unavailable"));
      }
      setCall((c) =>
        c ? { ...c, videoOn: room.videoOn, sharing: room.sharing } : c
      );
    })();
  }, [t]);

  /** Pull a colleague into the call. The call becomes a group call for everyone
   *  already in it — they follow by themselves — and the newcomer gets a ring
   *  of their own. */
  const invite = useCallback(
    (user: { id: string; name: string }) => {
      const current = callRef.current;
      const room = roomRef.current;
      if (!current || !room) return;
      void (async () => {
        try {
          const promoted = await room.promoteToGroup();
          if (!promoted) {
            toast.error(t("call_group_unavailable"));
            return;
          }
          setCall((c) => (c ? { ...c, group: true } : c));
          const { delivered } = await inviteToCall(
            current.companyId,
            current.conversationId,
            { callId: current.callId, userId: user.id }
          );
          toast.info(
            delivered
              ? t("call_invited", {
                  name: user.name,
                  defaultValue: `${user.name} invited`,
                })
              : t("call_offline")
          );
        } catch {
          toast.error(t("call_failed"));
        }
      })();
    },
    [t]
  );

  // A reload should not cost anyone the conversation. If this very tab was in
  // a call moments ago, go back into it as soon as the engine is ready — the
  // other side is holding the line for exactly this long.
  useEffect(() => {
    if (!enabled || callRef.current) return;
    const record = takeRememberedCall();
    if (!record) return;
    if (record.group) {
      resumeGroup(record);
    } else if (record.peer) {
      // a 1:1 has nothing to re-join: ring back with the SAME call id, which
      // is how the other side knows to answer in silence
      startCall(
        record.companyId,
        record.conversationId,
        record.peer,
        record.callId
      );
    }
  }, [enabled, resumeGroup, startCall]);

  /** A link for someone who has no ERP account at all — a client waiting to be
   *  shown something. Like inviting a colleague, it turns the call into a group
   *  call first: a guest joins the room, they do not get rung. */
  const guestLink = useCallback(async (): Promise<string | null> => {
    const current = callRef.current;
    const room = roomRef.current;
    if (!current || !room) return null;
    try {
      const promoted = await room.promoteToGroup();
      if (!promoted) {
        toast.error(t("call_group_unavailable"));
        return null;
      }
      setCall((c) => (c ? { ...c, group: true } : c));
      const link = await createGuestLink(current.companyId, current.conversationId, {
        callId: current.callId,
      });
      return link.url || `${window.location.origin}${link.path}`;
    } catch {
      toast.error(t("call_failed"));
      return null;
    }
  }, [t]);

  // ── ring channel (our own socket) ─────────────────────────────────────────
  useEffect(() => {
    if (!myUserId) return undefined;

    const onIncoming = (...args: unknown[]) => {
      const data = args[0] as IncomingPayload;
      if (!data?.callId || !data.from?.userId) return;
      const held = callRef.current;
      if (
        held &&
        held.reconnecting &&
        held.callId === String(data.callId) &&
        held.peer?.userId === String(data.from.userId)
      ) {
        resumeAsCallee(data);
        return;
      }
      if (callRef.current) {
        // already on a call — refuse politely instead of stacking two mics
        void sendCallSignal(data.companyId, data.conversationId, {
          type: "decline",
          callId: data.callId,
          reason: "busy",
          to: String(data.from.userId),
        }).catch(() => {});
        return;
      }
      const next: ActiveCall = {
        callId: data.callId,
        companyId: String(data.companyId),
        conversationId: String(data.conversationId),
        peer: { userId: String(data.from.userId), name: data.from.name || "" },
        outgoing: false,
        invited: !!data.group,
        reconnecting: false,
        group: !!data.group,
        rosterSeen: false,
        state: "incoming",
        everConnected: false,
        peerMedia: null,
        muted: false,
        videoOn: false,
        sharing: false,
        participants: [],
      };
      setCall(next);
      callRef.current = next;
      // the ring screen covers it anyway, and whatever it said is stale now
      setFailure(null);
      sdkRef.current?.startRingtone();
      void showBrowserNotification({
        title: data.group ? t("call_invite_title") : t("call_incoming_title"),
        body: next.peer?.name || "",
        tag: `erp-call-${next.callId}`,
      });
    };

    const onCancelled = (...args: unknown[]) => {
      const data = args[0] as { callId?: string };
      const current = callRef.current;
      if (!current || current.callId !== data?.callId) return;
      const wasRinging = current.state === "incoming";
      teardown(false);
      // "missed" only if we never picked up — a cancel arriving after the call
      // connected is just the peer's hangup reaching us by the other road
      if (wasRinging) toast.info(t("call_missed"));
    };

    const onDeclined = (...args: unknown[]) => {
      const data = args[0] as { callId?: string; reason?: string };
      const current = callRef.current;
      if (!current || current.callId !== data?.callId) return;
      // Once this is a group call, a decline can only be someone turning down
      // an invitation — it says nothing about the call itself, which carries on
      // with whoever is already in it. (Not gated on `connected`: the invite may
      // be declined while our own link to the group is still coming up.)
      if (current.group) {
        toast.info(t("call_declined"));
        return;
      }
      toast.info(data?.reason === "busy" ? t("call_busy") : t("call_declined"));
      teardown(false);
    };

    /** Their page is reloading. Keep the call standing and say so, rather than
     *  ending something that is about to continue. */
    const onReconnecting = (...args: unknown[]) => {
      const data = args[0] as { callId?: string };
      const current = callRef.current;
      if (!current || current.callId !== data?.callId) return;
      if (endedGraceRef.current) {
        clearTimeout(endedGraceRef.current);
        endedGraceRef.current = null;
      }
      setCall((c) => (c ? { ...c, reconnecting: true } : c));
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      // they get one window to come back; after that the call really is over
      resumeTimerRef.current = setTimeout(() => {
        resumeTimerRef.current = null;
        const cur = callRef.current;
        if (!cur || cur.callId !== data?.callId || !cur.reconnecting) return;
        toast.info(t("call_failed"));
        teardown(false);
      }, RESUME_WINDOW_MS);
    };

    socket.on("call-incoming", onIncoming);
    socket.on("call-cancelled", onCancelled);
    socket.on("call-declined", onDeclined);
    socket.on("call-reconnecting", onReconnecting);
    return () => {
      socket.off("call-incoming", onIncoming);
      socket.off("call-cancelled", onCancelled);
      socket.off("call-declined", onDeclined);
      socket.off("call-reconnecting", onReconnecting);
    };
  }, [myUserId, resumeAsCallee, t, teardown]);

  // nothing ever stays half-connected: a call that has not reached `connected`
  // within the backstop is torn down (and the peer told, if they were ringing)
  useEffect(() => {
    if (!call || call.state === "connected") return undefined;
    const sdk = sdkRef.current;
    const stuckMs =
      sdk && "CONNECT_TIMEOUT_MS" in sdk
        ? STUCK_TIMEOUT_MS
        : STUCK_TIMEOUT_LEGACY_MS;
    // A ring the caller stopped at 45 s but whose cancel never reached us must
    // not keep a full-screen call up for the whole connect backstop.
    const ms =
      call.state === "incoming" ? RING_TIMEOUT_MS + INCOMING_GRACE_MS : stuckMs;
    const id = setTimeout(() => {
      const current = callRef.current;
      if (!current || current.state === "connected") return;
      if (current.state === "incoming") {
        // a ring nobody in THIS tab answered: drop the screen quietly. Sending
        // a decline here would hang up the call a sibling tab may have taken,
        // and the caller already has its own no-answer timeout
        toast.info(t("call_missed"));
        teardown(false);
        return;
      }
      // still on our own microphone prompt after all this time: nobody is
      // going to answer it, and that — not the network — is what to fix
      recordFailure(current.state === "permission" ? "mic-pending" : "network");
      decline();
    }, ms);
    return () => clearTimeout(id);
  }, [call, decline, recordFailure, teardown, t]);

  // tick the in-call timer
  useEffect(() => {
    if (call?.state !== "connected") return undefined;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [call?.state]);

  // The ringtone runs on an AudioContext, which browsers keep suspended until
  // the page has seen a gesture. A colleague who is merely reading the ERP has
  // made no gesture we control, so arm it on the first one they make anywhere —
  // otherwise their phone rings silently and only the screen gives it away.
  useEffect(() => {
    const arm = () => {
      sdkRef.current?.unlockSound();
      window.removeEventListener("pointerdown", arm);
      window.removeEventListener("keydown", arm);
    };
    window.addEventListener("pointerdown", arm, { once: true });
    window.addEventListener("keydown", arm, { once: true });
    return () => {
      window.removeEventListener("pointerdown", arm);
      window.removeEventListener("keydown", arm);
    };
  }, [enabled]);

  // sibling tabs of the same person: whoever handles the ring silences the rest
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return undefined;
    const ch = new BroadcastChannel(TAB_CHANNEL);
    tabChannelRef.current = ch;
    ch.onmessage = (e: MessageEvent) => {
      const handled = (e.data as { handledCallId?: string })?.handledCallId;
      const current = callRef.current;
      // only drop a call we have not answered ourselves
      if (!handled || !current || current.callId !== handled) return;
      if (current.state !== "incoming") return;
      teardown(false);
    };
    return () => {
      tabChannelRef.current = null;
      ch.close();
    };
  }, [teardown]);

  // A reload mid-call must not leave the other side hanging. A connected call
  // is covered by the call-socket hangup; a call still ringing is not — the
  // callee has not even joined that socket yet — so that one goes out as an
  // app-level signal that can outlive the page.
  useEffect(() => {
    const onUnload = () => {
      const current = callRef.current;
      if (!current) return;
      if (current.state !== "connected") {
        // never became a call: the other side is still ringing and has to be
        // told, or their phone rings at nobody
        sendCallSignalBeacon(current.companyId, current.conversationId, {
          type: current.outgoing ? "cancel" : "decline",
          callId: current.callId,
          to: current.peer?.userId,
        });
        void roomRef.current?.hangUp(true);
        return;
      }
      // A reload is not a hang-up. Leave a note for our own next life, and ask
      // the other side to hold the line rather than watch the call die.
      rememberCall(current);
      sendCallSignalBeacon(current.companyId, current.conversationId, {
        type: "reload",
        callId: current.callId,
        to: current.peer?.userId,
      });
      // tear our own side down WITHOUT telling them: that message would end
      // the call we are about to come back to
      void roomRef.current?.hangUp(false);
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, []);

  const value = useMemo(
    () => ({
      enabled,
      busyConversationId: call?.conversationId ?? null,
      startCall,
    }),
    [enabled, call?.conversationId, startCall]
  );

  // who is still invitable: online colleagues who are not already here
  const invitable = useMemo(() => {
    if (!call) return [];
    const here = new Set<string>([
      myUserId,
      ...(call.peer ? [call.peer.userId] : []),
      ...call.participants.map((p) => p.userId),
    ]);
    return onlineUsers.filter((u) => !here.has(String(u.id)));
  }, [call, myUserId, onlineUsers]);

  return (
    <CallsContext.Provider value={value}>
      {children}
      {call && !call.outgoing && call.state === "incoming" && (
        <IncomingCallScreen
          peer={call.peer}
          invited={call.invited}
          onAccept={accept}
          onDecline={decline}
        />
      )}
      {/* Everyone's audio, mounted for the life of the CALL rather than the
          life of the panel: minimizing it, or a tile without a camera, must
          never be what makes a colleague go silent. The tiles carry picture
          only. */}
      {/* Before the call panel on purpose: a new call started from somewhere
          else sits on top of the stale failure instead of under it. */}
      {failure && (
        <CallFailurePanel
          key={failure.at}
          failure={failure}
          getSdk={() => sdkRef.current}
          onCallAgain={
            failure.peer && !call
              ? () =>
                  startCall(
                    failure.companyId,
                    failure.conversationId,
                    failure.peer as CallPeer
                  )
              : null
          }
          onClose={() => setFailure(null)}
        />
      )}
      {call &&
        Object.entries(streams).map(([peerId, stream]) => (
          <AudioSink key={peerId} stream={stream} />
        ))}
      {call && (call.outgoing || call.state !== "incoming") && (
        <CallPanel
          call={call}
          seconds={seconds}
          streams={streams}
          peerVideo={peerVideo}
          localStream={localStream}
          minimized={minimized}
          invitable={invitable}
          onMinimize={() => setMinimized((m) => !m)}
          onToggleMute={toggleMute}
          onToggleCamera={toggleCamera}
          onToggleScreen={toggleScreen}
          onInvite={invite}
          onGuestLink={guestLink}
          onHangUp={decline}
        />
      )}
    </CallsContext.Provider>
  );
}

function formatSeconds(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function IncomingCallScreen({
  peer,
  invited,
  onAccept,
  onDecline,
}: {
  peer: CallPeer | null;
  invited: boolean;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-[320px] rounded-2xl bg-gray-800 p-6 text-center shadow-2xl">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-600/20">
          <Phone className="h-7 w-7 animate-pulse text-green-400" />
        </div>
        <div className="text-sm text-gray-400">
          {invited ? t("call_invite_title") : t("call_incoming_title")}
        </div>
        <div className="mt-1 truncate text-lg font-semibold text-gray-100">
          {peer?.name}
        </div>
        <div className="mt-6 flex justify-center gap-3">
          <button
            onClick={onDecline}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-500"
            title={t("call_decline")}
          >
            <PhoneOff className="h-5 w-5" />
          </button>
          <button
            onClick={onAccept}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-green-600 text-white hover:bg-green-500"
            title={t("call_accept")}
          >
            <Phone className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// 44px round buttons for a finger on a phone, 32px from the md breakpoint up.
const CONTROL_BUTTON =
  "flex h-11 w-11 items-center justify-center rounded-full md:h-8 md:w-8";

function CallPanel({
  call,
  seconds,
  streams,
  peerVideo,
  localStream,
  minimized,
  invitable,
  onMinimize,
  onToggleMute,
  onToggleCamera,
  onToggleScreen,
  onInvite,
  onGuestLink,
  onHangUp,
}: {
  call: ActiveCall;
  seconds: number;
  streams: Record<string, MediaStream>;
  peerVideo: Record<string, boolean>;
  localStream: MediaStream | null;
  minimized: boolean;
  invitable: { id: string; name: string }[];
  onMinimize: () => void;
  onToggleMute: () => void;
  onToggleCamera: () => void;
  onToggleScreen: () => void;
  onInvite: (user: { id: string; name: string }) => void;
  onGuestLink: () => Promise<string | null>;
  onHangUp: () => void;
}) {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);
  const [stagePeer, setStagePeer] = useState<string | null>(null);
  const localRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = localRef.current;
    if (!el) return;
    if (el.srcObject !== (localStream ?? null))
      el.srcObject = localStream ?? null;
  }, [localStream]);

  // Whoever was being watched stopped sharing, or left: close the big view
  // rather than leave a frozen last frame filling the screen.
  useEffect(() => {
    if (stagePeer && !streams[stagePeer]) setStagePeer(null);
  }, [stagePeer, streams]);

  // The prompt is usually answered before anyone could read a hint about it
  // (a remembered "allow"); only one that lingers gets the explanation.
  const [permissionSlow, setPermissionSlow] = useState(false);
  useEffect(() => {
    if (call.state !== "permission") {
      setPermissionSlow(false);
      return undefined;
    }
    const id = setTimeout(() => setPermissionSlow(true), PERMISSION_HINT_DELAY_MS);
    return () => clearTimeout(id);
  }, [call.state]);

  // Which microphone prompt the call is waiting on, if any — named on screen
  // only once it has lasted long enough to be a real wait.
  const waitingOn: "ours" | "theirs" | null =
    call.state === "permission"
      ? "ours"
      : call.state === "connecting" && call.peerMedia === "permission"
      ? "theirs"
      : null;
  const [waitShown, setWaitShown] = useState<"ours" | "theirs" | null>(null);
  useEffect(() => {
    setWaitShown(null);
    if (!waitingOn) return undefined;
    const id = setTimeout(
      () => setWaitShown(waitingOn),
      PERMISSION_LABEL_DELAY_MS
    );
    return () => clearTimeout(id);
  }, [waitingOn]);
  const waitLabel = waitingOn && waitShown === waitingOn ? waitingOn : null;

  // Whose move it is, as precisely as we know: our own microphone prompt, the
  // other person's, or neither (then it really is the network connecting).
  const label = call.reconnecting
    ? t("call_reconnecting")
    : call.state === "connected"
    ? formatSeconds(seconds)
    : call.state === "ringing"
    ? t("call_ringing")
    : waitLabel === "ours"
    ? t("call_mic_permission")
    : waitLabel === "theirs"
    ? t("call_peer_mic_permission", {
        name: call.peer?.name || t("call_the_other_side"),
      })
    : t("call_connecting");

  // In a group call the roster is the truth about who is here; in a 1:1 it is
  // simply the person on the other end.
  const peerAsParticipant = call.peer
    ? [
        {
          userId: call.peer.userId,
          name: call.peer.name,
          video: false,
          screen: false,
        },
      ]
    : [];
  // The roster is the truth about a group call — but it arrives a moment after
  // we decide to become one, and until then the person already on the call is
  // still the person on the call. Falling back to them keeps their tile from
  // blinking out the instant someone presses "add"; once a roster HAS arrived
  // it is believed, including when it says everyone else has gone.
  const usingRoster = call.group && call.rosterSeen;
  const people = usingRoster ? call.participants : peerAsParticipant;
  const title = call.group
    ? t("call_group_title", { defaultValue: "Group call" })
    : call.peer?.name || "";
  const canShare = canShareScreen();

  const controls = (
    <>
      <button
        onClick={onToggleMute}
        disabled={call.state !== "connected"}
        className={`${CONTROL_BUTTON} bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40`}
        title={call.muted ? t("call_unmute") : t("call_mute")}
      >
        {call.muted ? (
          <MicOff className="h-4 w-4" />
        ) : (
          <Mic className="h-4 w-4" />
        )}
      </button>
      <button
        onClick={onToggleCamera}
        disabled={call.state !== "connected"}
        className={`${CONTROL_BUTTON} bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40`}
        title={
          call.videoOn && !call.sharing
            ? t("call_camera_off")
            : t("call_camera_on")
        }
      >
        {call.videoOn && !call.sharing ? (
          <VideoOff className="h-4 w-4" />
        ) : (
          <Video className="h-4 w-4" />
        )}
      </button>
      {canShare && (
        <button
          onClick={onToggleScreen}
          disabled={call.state !== "connected"}
          className={`${CONTROL_BUTTON} bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40`}
          title={call.sharing ? t("call_screen_stop") : t("call_screen_share")}
        >
          {call.sharing ? (
            <MonitorOff className="h-4 w-4" />
          ) : (
            <Monitor className="h-4 w-4" />
          )}
        </button>
      )}
      <div className="relative">
        <button
          onClick={() => setPicking((p) => !p)}
          disabled={call.state !== "connected"}
          className={`${CONTROL_BUTTON} bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40`}
          title={t("call_add_person")}
        >
          <UserPlus className="h-4 w-4" />
        </button>
        {picking && (
          <div className="absolute bottom-12 right-0 max-h-64 w-56 overflow-y-auto rounded-lg bg-gray-800 py-1 shadow-2xl ring-1 ring-gray-700 md:bottom-10">
            {invitable.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">
                {t("no_one_online")}
              </div>
            ) : (
              invitable.map((u) => (
                <button
                  key={u.id}
                  onClick={() => {
                    setPicking(false);
                    onInvite(u);
                  }}
                  className="block w-full truncate px-3 py-3 text-left text-sm text-gray-200 hover:bg-gray-700 md:py-2"
                >
                  {u.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      <button
        onClick={() => {
          void (async () => {
            const url = await onGuestLink();
            if (!url) return;
            try {
              await navigator.clipboard.writeText(url);
              toast.success(t("call_guest_link_copied"));
            } catch {
              // clipboard refused (no permission, insecure context): show the
              // link instead of pretending it was copied
              window.prompt(t("call_guest_link"), url);
            }
          })();
        }}
        disabled={call.state !== "connected"}
        className={`${CONTROL_BUTTON} bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40`}
        title={t("call_guest_link")}
      >
        <Link2 className="h-4 w-4" />
      </button>
      <button
        onClick={onHangUp}
        className={`${CONTROL_BUTTON} bg-red-600 text-white hover:bg-red-500`}
        title={t("call_hang_up")}
      >
        <PhoneOff className="h-4 w-4" />
      </button>
    </>
  );

  const staged = stagePeer
    ? people.find((p) => p.userId === stagePeer)
    : undefined;
  const stage = staged ? (
    <CallStage
      name={staged.name}
      // only a group roster says whether a picture is a screen or a camera;
      // in a 1:1 all we are told is that video is on
      screen={usingRoster ? !!staged.screen : false}
      stream={streams[staged.userId]}
      controls={controls}
      onClose={() => setStagePeer(null)}
    />
  ) : null;

  if (minimized) {
    return (
      <>
        {stage}
        {/* From the md breakpoint it is a centred pill in one row. On a phone it
            spans the screen in two rows (who, then buttons), so no button
            falls off an edge. */}
        <div className="fixed bottom-[calc(var(--phone-bar-height,0px)+1rem)] left-1/2 z-[9999] flex -translate-x-1/2 items-center gap-3 rounded-full bg-gray-800 px-4 py-2 text-sm text-gray-100 shadow-2xl max-md:left-2 max-md:right-2 max-md:translate-x-0 max-md:flex-col max-md:gap-2 max-md:rounded-2xl">
          {permissionSlow && (
            <div className="absolute bottom-full left-1/2 mb-2 w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg bg-amber-950 px-3 py-1.5 text-xs text-amber-100 shadow-xl ring-1 ring-amber-800">
              {t("call_mic_permission_prompt")}
            </div>
          )}
          <button
            onClick={onMinimize}
            className="flex items-center gap-2 max-md:max-w-full"
            title={t("expand")}
          >
            <Phone className="h-4 w-4 text-green-400" />
            <span className="max-w-[160px] truncate font-medium">{title}</span>
            <span className="max-w-[180px] truncate whitespace-nowrap tabular-nums text-gray-400">
              {label}
            </span>
          </button>
          {/* `contents` keeps the buttons direct children of the pill from md up */}
          <div className="flex flex-wrap items-center justify-center gap-2 md:contents">
            {controls}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {stage}
      <div className="fixed bottom-[calc(var(--phone-bar-height,0px)+1rem)] right-4 z-[9999] flex max-h-[calc(100dvh-var(--phone-bar-height,0px)-2rem)] w-[min(340px,calc(100vw-2rem))] flex-col rounded-2xl bg-gray-800 p-3 text-gray-100 shadow-2xl">
        <div className="mb-2 flex items-center gap-2">
          <Phone className="h-4 w-4 shrink-0 text-green-400" />
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {title}
          </span>
          <span className="max-w-[55%] shrink-0 truncate whitespace-nowrap tabular-nums text-xs text-gray-400">
            {label}
          </span>
          <button
            onClick={onMinimize}
            className="rounded p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-100 max-md:p-3"
            title={t("collapse")}
          >
            <X className="h-4 w-4 max-md:h-5 max-md:w-5" />
          </button>
        </div>

        {permissionSlow && (
          <div className="mb-2 rounded-lg bg-amber-950/60 p-2 text-xs text-amber-100 ring-1 ring-amber-900">
            <div className="font-medium">{t("call_mic_permission_prompt")}</div>
            <div className="mt-1 text-amber-200/80">
              {t("call_mic_permission_hidden")}
            </div>
            {/* no "reload": before the call connects that cancels it */}
            <MicHelpSteps
              className="mt-1 text-amber-200/80"
              withReload={false}
            />
          </div>
        )}

        {/* scrolls when the screen is too short for the pictures, so the buttons below stay in view */}
        <div
          className={`grid min-h-0 gap-2 overflow-y-auto ${
            people.length > 1 ? "grid-cols-2" : "grid-cols-1"
          }`}
        >
          {people.map((p) => (
            <PeerTile
              key={p.userId}
              name={p.name}
              stream={streams[p.userId]}
              // who has a picture is the roster's business once there IS one;
              // before that (a 1:1, or the moment just after "add") it is the
              // peer's own `call:video` signal — reading it off the stand-in
              // entry would hide a camera that is plainly on
              hasVideo={usingRoster ? !!p.video : !!peerVideo[p.userId]}
              screen={usingRoster ? !!p.screen : false}
              onExpand={() => setStagePeer(p.userId)}
            />
          ))}
          {localStream && (
            <div className="relative aspect-video overflow-hidden rounded-lg bg-gray-900 ring-1 ring-gray-700">
              <video
                ref={localRef}
                autoPlay
                playsInline
                muted
                className="h-full w-full object-cover"
              />
              <div className="absolute bottom-1 left-1 rounded bg-black/50 px-1.5 py-0.5 text-[11px] text-gray-100">
                {call.sharing ? t("call_your_screen") : t("call_you")}
              </div>
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          {controls}
        </div>
      </div>
    </>
  );
}
